/**
 * V2 admin · Handover protocol detail.
 *
 * Renders the same content the tenant submitted: summary, room photos,
 * inventory table, common-area photos, defects, signature. Includes a PDF
 * download that uses the shared client-side generator.
 *
 * A protocol an OPERATOR filed has none of those rows — its evidence is the PDF
 * the admin uploaded (a scanned paper protocol, phone photos), which lives in
 * storage and not in the photo/inventory/defect tables. This page used to render
 * the walkthrough sections regardless, so that protocol read as a walkthrough
 * that found nothing: "No photos", "No inventory items recorded", "No defects
 * reported" — three statements about a document whose contents were sitting one
 * signed URL away. So the filed document is embedded here, and the walkthrough
 * sections are shown only where there is a walkthrough to show.
 */
import { useEffect, useState } from "react";
import { baseLanguageFor } from "@/lib/i18n/languages";
import { useParams } from "react-router-dom";
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  Pencil,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { getSignedUrl, useAdminProtocolBundle } from "@/hooks/useProtocol";
import {
  COMMON_AREAS,
  isAdminRecorded,
  isAutoSubmitted,
  type ProtocolAttachment,
  type ProtocolPhoto,
  type ItemCondition,
  type CommonArea,
} from "@/lib/protocol";
import { downloadProtocolPdf, generateProtocolPdf } from "@/lib/protocol-pdf";

import { getProtocolKeyCopy } from "@/lib/protocol-key-copy";

type T = (key: string, fallback: string) => string;

function condLabel(t: T, condition: ItemCondition): string {
  switch (condition) {
    case "good":
      return t("admin.protocol.condition.good", "Good");
    case "wear":
      return t("admin.protocol.condition.wear", "Signs of wear");
    case "damaged":
      return t("admin.protocol.condition.damaged", "Damaged");
    default:
      return condition;
  }
}

const COND_TONE: Record<ItemCondition, string> = {
  good: "bg-emerald-100 text-emerald-700 border-emerald-200",
  wear: "bg-amber-100 text-amber-700 border-amber-200",
  damaged: "bg-rose-100 text-rose-700 border-rose-200",
};

function areaLabel(t: T, area: CommonArea): string {
  switch (area) {
    case "kitchen":
      return t("admin.protocol.area.kitchen", "Kitchen");
    case "bath":
      return t("admin.protocol.area.bathroom", "Bathroom");
    case "hallway":
      return t("admin.protocol.area.hallway", "Hallway");
    case "other":
      return t("admin.protocol.area.other", "Other");
    default:
      return area;
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ProtocolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, language, tDict } = useLanguage();
  // Narrowed: the protocol PDF renderer predates the six-language widening.
  const baseLang = baseLanguageFor(language);
  const { data, isLoading, error } = useAdminProtocolBundle(id);
  const [downloading, setDownloading] = useState(false);

  if (isLoading) {
    return (
      <AdminV2Layout
        title={t("admin.protocol.pageTitle", "Protocol")}
        breadcrumbs={[
          {
            label: t("admin.protocol.breadcrumb.admin", "Admin"),
            to: "/admin",
          },
          {
            label: t("admin.protocol.breadcrumb.tenants", "Tenants"),
            to: "/admin?tab=tenants",
          },
          { label: t("common.loading", "Loading…") },
        ]}
      >
        <div className="flex justify-center py-20 text-neutral-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </AdminV2Layout>
    );
  }

  if (error || !data?.protocol) {
    return (
      <AdminV2Layout
        title={t("admin.protocol.pageTitle", "Protocol")}
        breadcrumbs={[
          {
            label: t("admin.protocol.breadcrumb.admin", "Admin"),
            to: "/admin",
          },
          {
            label: t("admin.protocol.breadcrumb.tenants", "Tenants"),
            to: "/admin?tab=tenants",
          },
          { label: t("admin.protocol.breadcrumb.notFound", "Not found") },
        ]}
      >
        <Card className="p-12 text-center text-sm text-neutral-500">
          {t("admin.protocol.notFound", "Protocol not found.")}
        </Card>
      </AdminV2Layout>
    );
  }

  const { protocol, photos, inventory, defects, attachments, booking } = data;
  const roomPhotos = photos.filter((p) => p.category === "room");
  const keyPhotos = photos.filter((p) => p.category === "keys");
  const copy = getProtocolKeyCopy(language, tDict);
  // COL-425: an admin submitted this protocol on the tenant's behalf via the
  // attachments flow — distinct from isAdminRecorded()/isAutoSubmitted()
  // below, which cover an operator's paper recording or a tick's deeming.
  const adminSubmitted = Boolean(
    (protocol as any).admin_submitted_by ||
    (protocol as any).admin_submitted_note
  );
  const adminRecorded = isAdminRecorded(protocol);
  // Deemed submitted by the tick, not filed by an admin and not walked
  // through — a third state the "Recorded by operator" branch must not
  // absorb, since its own banner names a specific admin who is not the actor
  // here.
  const autoSubmitted = !adminRecorded && isAutoSubmitted(protocol);
  // Either way, nobody walked through this one — the photo/inventory/defect/
  // signature sections below are empty by definition, not because a real
  // walkthrough found nothing.
  const noWalkthrough = adminRecorded || autoSubmitted;

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);

    /**
     * An operator recording is NOT regenerated.
     *
     * The client-side generator renders a tenant walkthrough — photos, the
     * inventory table, the signature — none of which an operator recording has.
     * Run against one it produces a near-empty document that omits the very
     * evidence the admin attached, under the same reference number as the real
     * file. The stored PDF is the document; fetch that.
     */
    if (adminRecorded && protocol.pdf_storage_path) {
      const toastId = toast.loading(
        t(
          "admin.protocol.toast.fetchingFiledDocument",
          "Fetching the filed document…"
        )
      );
      try {
        const url = await getSignedUrl(protocol.pdf_storage_path);
        if (!url)
          throw new Error(
            t(
              "admin.protocol.error.noDownloadLink",
              "Could not produce a download link."
            )
          );
        const res = await fetch(url);
        if (!res.ok)
          throw new Error(
            t(
              "admin.protocol.error.fetchFiledDocumentFailed",
              "Could not fetch the filed document."
            )
          );
        downloadProtocolPdf(await res.blob(), protocol.ref);
        toast.success(
          `${protocol.ref}.pdf ${t("admin.protocol.downloadedSuffix", "downloaded")}`,
          { id: toastId }
        );
      } catch (err: any) {
        toast.error(
          err?.message ??
            t(
              "admin.protocol.error.downloadFailed",
              "Could not download the document"
            ),
          {
            id: toastId,
          }
        );
      } finally {
        setDownloading(false);
      }
      return;
    }

    // PDF generation fetches and embeds every photo as a data URL, which
    // can take 5–15s on protocols with many photos. Toast lets the admin
    // know nothing is broken; we resolve it to success/error below.
    const toastId = toast.loading(
      t("admin.protocol.toast.generatingPdf", "Generating PDF…"),
      {
        description: t(
          "admin.protocol.toast.generatingPdfDescription",
          "Embedding photos and signature — this may take a moment."
        ),
      }
    );
    try {
      const blob = await generateProtocolPdf({
        protocol,
        photos,
        inventory,
        defects,
        booking: booking
          ? {
              property_name: booking.property_name,
              room_number: booking.room_number,
              address: booking.address,
              move_in_date: booking.move_in_date ?? null,
              move_out_date: booking.move_out_date ?? null,
              deposit_amount: booking.deposit_amount ?? null,
            }
          : null,
        tenant: {
          name: booking?.tenant_name ?? null,
          email: booking?.tenant_email ?? null,
        },
        language: baseLang,
      });
      downloadProtocolPdf(blob, protocol.ref);
      toast.success(
        `${protocol.ref}.pdf ${t("admin.protocol.downloadedSuffix", "downloaded")}`,
        { id: toastId }
      );
    } catch (err: any) {
      toast.error(
        err?.message ??
          t("admin.protocol.error.generateFailed", "Could not generate PDF"),
        { id: toastId }
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <AdminV2Layout
      title={`${t("admin.protocol.pageTitle", "Protocol")} · ${protocol.ref}`}
      breadcrumbs={(() => {
        const crumbs: Array<{ label: string; to?: string }> = [
          {
            label: t("admin.protocol.breadcrumb.admin", "Admin"),
            to: "/admin",
          },
        ];
        // Build a tenant deep-link when we have the booking context.
        if (booking?.property_id && data.bookingRoute) {
          crumbs.push({
            label:
              booking.tenant_name ??
              booking.tenant_email ??
              t("admin.protocol.tenantFallback", "Tenant"),
            to: data.bookingRoute,
          });
        } else {
          crumbs.push({
            label: t("admin.protocol.breadcrumb.tenants", "Tenants"),
            to: "/admin?tab=tenants",
          });
        }
        crumbs.push({ label: protocol.ref });
        return crumbs;
      })()}
      actions={
        <Button
          onClick={handleDownload}
          disabled={downloading}
          className="gap-2"
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {t("admin.protocol.downloadPdf", "Download PDF")}
        </Button>
      }
    >
      {/* Header chips */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Badge
          className={
            adminRecorded || autoSubmitted
              ? "bg-amber-100 text-amber-800 border-amber-200"
              : protocol.status === "submitted"
                ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                : "bg-amber-100 text-amber-700 border-amber-200"
          }
        >
          {adminRecorded
            ? t(
                "admin.protocol.status.recordedByOperator",
                "Recorded by operator"
              )
            : autoSubmitted
              ? t("admin.protocol.status.autoSubmitted", "Auto-submitted")
              : protocol.status === "submitted"
                ? t("admin.protocol.status.submitted", "Submitted")
                : t("admin.protocol.status.inProgress", "In progress")}
        </Badge>
        <Badge variant="outline">
          {protocol.kind === "move_in"
            ? t("admin.protocol.moveIn", "Move-in")
            : t("admin.protocol.moveOut", "Move-out")}
        </Badge>
        <span className="text-sm text-neutral-500">
          {formatDate(protocol.submitted_at ?? protocol.created_at)}
        </span>
      </div>

      {/*
        An operator recording has no photos, no inventory and no signature, so
        without this the page reads as a walkthrough that found nothing rather
        than one that never happened.
      */}
      {adminRecorded && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="text-sm font-semibold text-amber-900">
            {t(
              "admin.protocol.recordedOnBehalf",
              "Recorded on the tenant's behalf"
            )}
            {protocol.recorded_by_name
              ? ` ${t("admin.protocol.byPrefix", "by")} ${protocol.recorded_by_name}`
              : ""}
            {protocol.recorded_at
              ? ` ${t("admin.protocol.onPrefix", "on")} ${formatDate(protocol.recorded_at)}`
              : ""}
          </div>
          <p className="mt-1 text-sm leading-snug text-amber-800">
            {t(
              "admin.protocol.recordedOnBehalfBody",
              "This is not a walkthrough the tenant completed and signed in the app, and carries no tenant signature. The documents the admin filed are in the protocol PDF."
            )}
          </p>
          {protocol.admin_note ? (
            <p className="mt-2 whitespace-pre-wrap text-sm text-amber-900">
              “{protocol.admin_note}”
            </p>
          ) : null}
        </div>
      )}

      {/*
        Nobody filed this one either — the window simply closed with no
        walkthrough, and the tick deemed the room accepted. Same caveat as an
        operator recording, different actor: there is no admin name to print.
      */}
      {autoSubmitted && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="text-sm font-semibold text-amber-900">
            {t(
              "admin.protocol.autoDeemedSubmitted",
              "Automatically deemed submitted"
            )}
            {protocol.auto_submitted_at
              ? ` ${t("admin.protocol.onPrefix", "on")} ${formatDate(protocol.auto_submitted_at)}`
              : ""}
          </div>
          <p className="mt-1 text-sm leading-snug text-amber-800">
            {t(
              "admin.protocol.autoDeemedSubmittedBody",
              "The submission window closed with no walkthrough on file. This is not a walkthrough the tenant completed and signed — the PDF is the attestation page, not a room inspection."
            )}
          </p>
        </div>
      )}

      {/* Summary */}
      <Card className="overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <SummaryRow
            label={t("admin.protocol.summary.property", "Property")}
            value={booking?.property_name ?? "—"}
          />
          <SummaryRow
            label={t("admin.protocol.summary.room", "Room")}
            value={
              booking?.room_number
                ? `${t("admin.activity.roomPrefix", "Room")} ${booking.room_number}`
                : "—"
            }
          />
          <SummaryRow
            label={t("admin.protocol.summary.address", "Address")}
            value={booking?.address ?? "—"}
          />
          <SummaryRow
            label={t("admin.protocol.summary.tenant", "Tenant")}
            value={
              booking?.tenant_name
                ? booking.tenant_name
                : booking?.tenant_email
                  ? booking.tenant_email
                  : "—"
            }
            extra={
              booking?.tenant_email ? (
                <a
                  href={`mailto:${booking.tenant_email}`}
                  className="ml-2 inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline"
                >
                  <Mail className="h-3 w-3" /> {booking.tenant_email}
                </a>
              ) : null
            }
          />
          <SummaryRow
            label={t("admin.protocol.summary.reference", "Reference")}
            value={protocol.ref}
            mono
          />
          <SummaryRow
            label={t(
              "admin.protocol.summary.createdSubmitted",
              "Created / Submitted"
            )}
            value={`${formatDate(protocol.created_at)}${
              protocol.submitted_at
                ? ` → ${formatDate(protocol.submitted_at)}`
                : ""
            }`}
          />
        </div>
      </Card>

      {/* COL-425: filed by an admin on the tenant's behalf — no signature,
          note and/or attachments are the evidence instead. */}
      {(adminSubmitted || attachments.length > 0) && (
        <Section
          title={t(
            "admin.protocol.submittedOnBehalf.title",
            "Submitted on the tenant's behalf"
          )}
          subtitle={`${attachments.length} ${t("admin.protocol.attachmentCountSuffix", "attachment(s)")}`}
        >
          <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              {t(
                "admin.protocol.submittedOnBehalf.body",
                "This protocol was filed by coliviq staff, not signed by the tenant. The note and attachments below are the evidence."
              )}
              {(protocol as any).admin_submitted_note ? (
                <p className="mt-1 whitespace-pre-wrap text-amber-900">
                  “{(protocol as any).admin_submitted_note}”
                </p>
              ) : null}
            </div>
          </div>
          {attachments.length ? (
            <ul className="space-y-1.5">
              {attachments.map((a) => (
                <AttachmentRow key={a.id} attachment={a} />
              ))}
            </ul>
          ) : (
            <EmptyText
              text={t(
                "admin.protocol.submittedOnBehalf.noAttachments",
                "No attachments — the note above is the only evidence filed."
              )}
            />
          )}
        </Section>
      )}

      {/* The document on file, readable here rather than only downloadable. */}
      {protocol.pdf_storage_path ? (
        <Section
          title={
            noWalkthrough
              ? t("admin.protocol.filedDocument.title", "Filed document")
              : t("admin.protocol.protocolPdf.title", "Protocol PDF")
          }
          subtitle={
            adminRecorded
              ? t(
                  "admin.protocol.filedDocument.subtitleOperator",
                  "Attestation page and everything the operator attached"
                )
              : autoSubmitted
                ? t(
                    "admin.protocol.filedDocument.subtitleAuto",
                    "Attestation page — the window closed with no walkthrough on file"
                  )
                : t(
                    "admin.protocol.filedDocument.subtitleGenerated",
                    "As generated on submission"
                  )
          }
        >
          <FiledDocument path={protocol.pdf_storage_path} ref_={protocol.ref} />
        </Section>
      ) : adminRecorded ? (
        <Section
          title={t("admin.protocol.filedDocument.title", "Filed document")}
        >
          <EmptyText
            text={t(
              "admin.protocol.filedDocument.emptyOperator",
              "No document on file for this recording — the deposit bundle stays blocked until one is attached. Use “Record protocol…” on the tenant's move-in or move-out tab to attach it."
            )}
          />
        </Section>
      ) : autoSubmitted ? (
        <Section
          title={t("admin.protocol.filedDocument.title", "Filed document")}
        >
          <EmptyText
            text={t(
              "admin.protocol.filedDocument.emptyAuto",
              "No document on file yet — the render failed after the deeming was recorded. The next tick run retries it."
            )}
          />
        </Section>
      ) : null}

      {/*
        The walkthrough itself. Skipped entirely for an operator recording or an
        auto-submitted one: these tables are empty by definition there, and "No
        photos" beside "0 items" describes a walkthrough that never happened
        rather than one that found nothing. A recording that somehow does carry
        rows still shows them.
      */}
      {(!noWalkthrough || photos.length > 0) && (
        <Section
          title={t("admin.protocol.roomPhotos.title", "Room photos")}
          subtitle={`${roomPhotos.length} ${t("admin.protocol.photoCountSuffix", "photo(s)")}`}
        >
          <PhotoStrip photos={roomPhotos} />
        </Section>
      )}

      {keyPhotos.length > 0 && (
        <Section
          title={
            protocol.kind === "move_in"
              ? copy.keyPickupTitle
              : copy.keyReturnTitle
          }
          subtitle={`${keyPhotos.length} ${t("admin.protocol.photoCountSuffix", "photo(s)")}`}
        >
          <PhotoStrip photos={keyPhotos} />
        </Section>
      )}

      {/* Inventory */}
      {(!noWalkthrough || inventory.length > 0) && (
        <Section
          title={t("admin.protocol.inventory.title", "Inventory")}
          subtitle={`${inventory.length} ${t("admin.protocol.itemCountSuffix", "item(s)")}`}
        >
          {inventory.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th className="px-4 py-3">
                      {t("admin.protocol.inventory.item", "Item")}
                    </th>
                    <th className="px-4 py-3">
                      {t("admin.protocol.inventory.condition", "Condition")}
                    </th>
                    <th className="px-4 py-3">
                      {t("admin.protocol.inventory.notes", "Notes")}
                    </th>
                    <th className="px-4 py-3">
                      {t("admin.protocol.inventory.photo", "Photo")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {inventory.map((it) => (
                    <tr key={it.id} className="border-t border-neutral-100">
                      <td className="px-4 py-3">
                        <div className="font-medium text-neutral-900 flex items-center gap-2">
                          <Pencil className="h-3.5 w-3.5 text-neutral-400" />
                          {it.label}
                        </div>
                        {it.meta ? (
                          <div className="text-xs text-neutral-500">
                            {it.meta}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        {it.condition ? (
                          <Badge className={COND_TONE[it.condition]}>
                            {condLabel(t, it.condition)}
                          </Badge>
                        ) : (
                          <span className="text-xs text-neutral-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700">
                        {it.defect_description ? (
                          <span>
                            {it.defect_description}
                            {it.defect_severity ? (
                              <span className="ml-1 text-xs text-rose-600">
                                ({it.defect_severity})
                              </span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="text-xs text-neutral-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {it.photo_storage_path ? (
                          <InlineThumb path={it.photo_storage_path} />
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-400">
                            <Camera className="h-3 w-3" /> —
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyText
              text={t(
                "admin.protocol.inventory.empty",
                "No inventory items recorded."
              )}
            />
          )}
        </Section>
      )}

      {/* Common areas */}
      {COMMON_AREAS.map((area) => {
        const areaPhotos = photos.filter((p) => p.category === area.id);
        if (!areaPhotos.length) return null;
        return (
          <Section
            key={area.id}
            title={`${t("admin.protocol.commonAreaPrefix", "Common area")} · ${areaLabel(t, area.id)}`}
            subtitle={`${areaPhotos.length} ${t("admin.protocol.photoCountSuffix", "photo(s)")}`}
          >
            <PhotoStrip photos={areaPhotos} />
          </Section>
        );
      })}

      {/* Defects */}
      {(!noWalkthrough || defects.length > 0) && (
        <Section
          title={t("admin.protocol.defects.title", "Reported defects")}
          subtitle={`${defects.length} ${t("admin.protocol.defectCountSuffix", "defect(s)")}`}
        >
          {defects.length ? (
            <ul className="space-y-2">
              {defects.map((d) => (
                <li
                  key={d.id}
                  className="flex items-start gap-3 rounded-xl border border-neutral-200 p-3"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-neutral-900">
                        {areaLabel(t, d.area)}
                      </span>
                      <Badge
                        className={
                          d.severity === "high"
                            ? "bg-rose-100 text-rose-700 border-rose-200"
                            : d.severity === "medium"
                              ? "bg-amber-100 text-amber-700 border-amber-200"
                              : "bg-emerald-100 text-emerald-700 border-emerald-200"
                        }
                      >
                        {d.severity === "high"
                          ? t("admin.activity.severity.high", "high")
                          : d.severity === "medium"
                            ? t("admin.activity.severity.medium", "medium")
                            : t("admin.activity.severity.low", "low")}
                      </Badge>
                    </div>
                    <div className="mt-0.5 text-sm text-neutral-700">
                      {d.description}
                    </div>
                  </div>
                  {d.photo_storage_path ? (
                    <InlineThumb path={d.photo_storage_path} />
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyText
              text={t("admin.protocol.defects.empty", "No defects reported.")}
            />
          )}
        </Section>
      )}

      {/* Signature. An operator recording or an auto-submitted one carries none
          by definition — the amber banner above already states that, so an
          empty section would only repeat it. */}
      {(!noWalkthrough || protocol.signature_data) && (
        <Section title={t("admin.protocol.signature.title", "Signature")}>
          {protocol.signature_data ? (
            <SignatureBlock
              dataUrl={protocol.signature_data}
              signedAt={protocol.signed_at}
              tenantName={booking?.tenant_name ?? booking?.tenant_email ?? null}
            />
          ) : adminRecorded ? (
            <EmptyText
              text={t(
                "admin.protocol.signature.emptyOperator",
                "No tenant signature — this protocol was recorded by an operator on the tenant's behalf."
              )}
            />
          ) : autoSubmitted ? (
            <EmptyText
              text={t(
                "admin.protocol.signature.emptyAuto",
                "No tenant signature — the window closed with no walkthrough submitted."
              )}
            />
          ) : (
            <EmptyText
              text={t(
                "admin.protocol.signature.emptyGeneral",
                "No signature captured."
              )}
            />
          )}
        </Section>
      )}
    </AdminV2Layout>
  );
}

/**
 * The stored protocol PDF, embedded.
 *
 * An <object> rather than an <img> or a link: the file is a PDF (often a scan of
 * a signed paper protocol, sometimes many pages), and the browser's own viewer is
 * the only thing here that can render it. Every browser this admin surface
 * supports has one; the fallback inside the element covers the ones that do not
 * and any signing failure, so the way to the document is never lost.
 *
 * The URL is signed and short-lived — the bucket is private — so it is fetched on
 * mount rather than composed from the path.
 */
function FiledDocument({ path, ref_ }: { path: string; ref_: string }) {
  const { t } = useLanguage();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    setUrl(null);
    getSignedUrl(path).then((u) => {
      if (!alive) return;
      if (u) setUrl(u);
      else setFailed(true);
    });
    return () => {
      alive = false;
    };
  }, [path]);

  if (failed) {
    return (
      <EmptyText
        text={t(
          "admin.protocol.filedDocument.linkFailed",
          "Could not produce a link to the document just now. Reload the page, or use Download PDF above."
        )}
      />
    );
  }
  if (!url) {
    return (
      <div className="flex h-[36rem] items-center justify-center text-neutral-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <object
        data={url}
        type="application/pdf"
        title={`${ref_} ${t("admin.protocol.filedDocument.documentSuffix", "document")}`}
        className="h-[36rem] w-full rounded-xl border border-neutral-200 bg-neutral-50"
      >
        <div className="p-6 text-center text-sm text-neutral-500">
          {t(
            "admin.protocol.filedDocument.cannotDisplayInline",
            "This browser cannot display the PDF inline."
          )}
        </div>
      </object>
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="outline" size="sm" className="gap-2">
          <a href={url} target="_blank" rel="noreferrer">
            <ExternalLink className="h-4 w-4" />
            {t("admin.deposit.openInNewTab", "Open in a new tab")}
          </a>
        </Button>
        <Button asChild variant="outline" size="sm" className="gap-2">
          <a href={url} download={`${ref_}.pdf`}>
            <Download className="h-4 w-4" />
            {t("common.download", "Download")}
          </a>
        </Button>
      </div>
    </div>
  );
}

/**
 * Detects whether a captured signature data URL has any visible ink and
 * either renders it or falls back to a printed-name attestation when the
 * canvas was empty (an old-bug protocol with `signed_at` but no strokes).
 */
function SignatureBlock({
  dataUrl,
  signedAt,
  tenantName,
}: {
  dataUrl: string;
  signedAt: string | null;
  tenantName: string | null;
}) {
  const { t } = useLanguage();
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      try {
        const cvs = document.createElement("canvas");
        cvs.width = img.naturalWidth || 1;
        cvs.height = img.naturalHeight || 1;
        const ctx = cvs.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0);
        const { data: pixels } = ctx.getImageData(0, 0, cvs.width, cvs.height);
        // Count opaque, non-white pixels — a captured signature has many.
        let inkCount = 0;
        for (let i = 0; i < pixels.length; i += 4) {
          const a = pixels[i + 3];
          if (a > 16) {
            inkCount++;
            if (inkCount > 50) break;
          }
        }
        if (!cancelled) setEmpty(inkCount <= 50);
      } catch {
        // Cross-origin tainted — assume rendered fine.
      }
    };
    img.src = dataUrl;
    return () => {
      cancelled = true;
    };
  }, [dataUrl]);

  return (
    <div className="inline-block max-w-full rounded-xl border border-neutral-200 bg-white p-3">
      {empty ? (
        <div className="flex w-72 flex-col items-start gap-2 px-3 py-6">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
            {t(
              "admin.protocol.signature.digitalAttestation",
              "Signed (digital attestation)"
            )}
          </span>
          <span
            className="text-2xl text-neutral-900"
            style={{
              fontFamily: "'Brush Script MT', 'Comic Sans MS', cursive",
            }}
          >
            {tenantName ?? t("admin.protocol.tenantFallback", "Tenant")}
          </span>
        </div>
      ) : (
        <img
          src={dataUrl}
          alt={t(
            "admin.protocol.signature.tenantSignatureAlt",
            "Tenant signature"
          )}
          className="block h-32 w-auto max-w-full"
        />
      )}
      {signedAt ? (
        <div className="mt-1 flex items-center gap-1.5 px-1 text-xs text-neutral-500">
          <CheckCircle2 className="h-3 w-3 text-emerald-500" />
          {t("admin.activity.protocol.signed", "Signed")} {formatDate(signedAt)}
        </div>
      ) : null}
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-8">
      <div className="mb-3 flex items-end justify-between">
        <h2 className="text-base font-semibold text-neutral-900">{title}</h2>
        {subtitle ? (
          <span className="text-xs uppercase tracking-wider text-neutral-400">
            {subtitle}
          </span>
        ) : null}
      </div>
      <Card className="p-4">{children}</Card>
    </section>
  );
}

function SummaryRow({
  label,
  value,
  mono,
  extra,
}: {
  label: string;
  value: string;
  mono?: boolean;
  extra?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3 last:border-b-0 md:[&:nth-last-child(2)]:border-b-0">
      <span className="text-xs uppercase tracking-wider text-neutral-500">
        {label}
      </span>
      <span
        className={`text-sm font-semibold text-neutral-900 ${mono ? "font-mono" : ""}`}
      >
        {value}
        {extra}
      </span>
    </div>
  );
}

function EmptyText({ text }: { text: string }) {
  return (
    <div className="py-4 text-center text-sm text-neutral-400">{text}</div>
  );
}

function PhotoStrip({ photos }: { photos: ProtocolPhoto[] }) {
  const { t } = useLanguage();
  if (!photos.length)
    return <EmptyText text={t("admin.protocol.noPhotos", "No photos.")} />;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
      {photos.map((p) => (
        <PhotoTile key={p.id} photo={p} />
      ))}
    </div>
  );
}

function PhotoTile({ photo }: { photo: ProtocolPhoto }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    getSignedUrl(photo.storage_path).then((u) => alive && setUrl(u));
    return () => {
      alive = false;
    };
  }, [photo.storage_path]);
  return (
    <a
      href={url ?? "#"}
      target="_blank"
      rel="noreferrer"
      className="block aspect-square overflow-hidden rounded-xl bg-neutral-100"
    >
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full items-center justify-center text-neutral-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      )}
    </a>
  );
}

function AttachmentRow({ attachment }: { attachment: ProtocolAttachment }) {
  const { t } = useLanguage();
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const open = async () => {
    if (url) {
      window.open(url, "_blank", "noreferrer");
      return;
    }
    // Open the tab synchronously inside the click's user gesture so it isn't
    // blocked by the popup blocker, then point it at the signed URL once
    // resolved.
    const newTab = window.open("", "_blank", "noreferrer");
    setLoading(true);
    try {
      const signed = await getSignedUrl(attachment.storage_path);
      if (signed) {
        setUrl(signed);
        if (newTab) newTab.location.href = signed;
        else window.open(signed, "_blank", "noreferrer");
      } else {
        newTab?.close();
        toast.error(
          t(
            "admin.protocol.error.attachmentOpenFailed",
            "Could not open this attachment."
          )
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <li className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
      <FileText className="h-4 w-4 shrink-0 text-neutral-400" />
      <span className="flex-1 truncate">
        {attachment.file_name ?? attachment.storage_path}
      </span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={loading}
        onClick={() => void open()}
        className="gap-1.5"
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Download className="h-3.5 w-3.5" />
        )}
        {t("admin.protocol.open", "Open")}
      </Button>
    </li>
  );
}

function InlineThumb({ path }: { path: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    getSignedUrl(path).then((u) => alive && setUrl(u));
    return () => {
      alive = false;
    };
  }, [path]);
  return (
    <a
      href={url ?? "#"}
      target="_blank"
      rel="noreferrer"
      className="block h-12 w-12 overflow-hidden rounded-lg bg-neutral-100"
    >
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" />
      ) : null}
    </a>
  );
}
